import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import { createMentionExtension } from '../extensions/mention';
import { useDismissibleLayer } from '../hooks/useDismiss';
import type { ChannelMember } from '../lib/api';

interface Props {
  initialContent: string;
  onSave: (markdown: string) => void;
  onCancel: () => void;
  disabled?: boolean;
  users: ChannelMember[];
}

export default function EditEditor({ initialContent, onSave, onCancel, disabled, users }: Props) {
  const usersRef = useRef(users);
  usersRef.current = users;

  const mentionExtension = useMemo(
    () => createMentionExtension(() => usersRef.current),
    [],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Markdown.configure({
        html: false,
        transformCopiedText: true,
        transformPastedText: true,
      }),
      mentionExtension,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap-editor tiptap-edit-editor',
      },
    },
  });

  useEffect(() => {
    if (editor && initialContent) {
      editor.commands.setContent(initialContent);
      editor.commands.focus('end');
    }
  }, [editor, initialContent]);

  const getMarkdown = useCallback(() => {
    if (!editor) return '';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mdStorage = (editor.storage as any).markdown as { getMarkdown(): string } | undefined;
    return mdStorage?.getMarkdown() ?? editor.getText();
  }, [editor]);

  const handleSave = useCallback(() => {
    const md = getMarkdown().trim();
    if (md) onSave(md);
  }, [getMarkdown, onSave]);

  /**
   * An edit in progress is a layer of its own, but only for as long as the
   * keyboard is in it: it is drawn inline in a message row rather than over the
   * app, and nothing takes it down when the member goes and types somewhere
   * else. Escape pressed in the channel composer, or in a thread, is that
   * surface's — a docked thread to dismiss, a menu to close — and answering it
   * here would throw away text the member cannot get back. While the edit does
   * hold the keyboard, the key stops here and does not also reach whatever the
   * message is being edited on top of.
   */
  const containerRef = useRef<HTMLDivElement>(null);
  const [holdsFocus, setHoldsFocus] = useState(false);
  useDismissibleLayer({ active: holdsFocus, within: containerRef, onEscape: onCancel });

  useEffect(() => {
    if (!editor) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSave();
      }
    };
    const el = editor.view.dom;
    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
  }, [editor, handleSave]);

  return (
    <div
      ref={containerRef}
      className="message-edit-container"
      onFocus={() => setHoldsFocus(true)}
      onBlur={event => setHoldsFocus(event.currentTarget.contains(event.relatedTarget))}
    >
      <div className="tiptap-wrapper tiptap-edit-wrapper">
        <EditorContent editor={editor} />
      </div>
      <div className="message-edit-hint">
        Ctrl+Enter 保存 · Esc 取消
        {disabled && ' · 保存中...'}
      </div>
    </div>
  );
}
